import { useCallback, useState } from 'react'

import { isEmpty } from 'ramda'

import { ArticleOutlined, FileDownload, ImageOutlined } from '@mui/icons-material'
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'

import html2pdf from 'html2pdf.js'

import { downloadFile, sanitizeFileName } from '@common/utils'
import { formatCSVData } from '@rest/Utils/csv'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object} props.property
 * @param {Object} props.data
 * @param {string} props.layer
 * @param {boolean} props.disabled
 * @param {Function} props.onChangePDFExportStatus
 */
export default function MapExportButton({
  property,
  data,
  layer,
  disabled,
  pdfOptions,
  ...rest
}) {
  const { onChangePDFExportStatus, width, height } = pdfOptions

  const [loading, setLoading] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const handleExportData = useCallback(() => {
    setLoading(true)
    try {
      const csvData = formatCSVData(data, (unit) => {
        const haveDevices = !isEmpty(unit.devices)
        const getDeviceHeader = (prop, fallback) =>
          haveDevices
            ? unit.devices.map((_, i) => `devices.${i}.${prop}`)
            : fallback ?? prop

        return {
          name: 'Name',
          floor: 'Floor',
          status: 'Status',
          [getDeviceHeader('mainMac')]: 'MAC Address',
          [getDeviceHeader('status', 'deviceStatus')]: 'Device Status',
          [getDeviceHeader('awsStatus')]: 'AWS Status',
          [getDeviceHeader('rssi')]: 'RSSI Strength',
          [getDeviceHeader('ap')]: 'Access Point',
          [getDeviceHeader('ssid')]: 'SSID',
          [getDeviceHeader('ssidMigrationStatus')]: 'SSID Migration Status',
          [getDeviceHeader('fw')]: 'Firmware',
          [getDeviceHeader('hasMicrophone')]: 'Has Microphone',
          [getDeviceHeader('noiseEnabled')]: 'Noise Enabled',
        }
      })
      downloadFile({
        data: [csvData],
        fileName: `${property.name}_connectivity_map_data.csv`,
        fileType: 'text/csv',
      })
    } finally {
      setLoading(false)
    }
  }, [data, property])

  const handleExportMap = useCallback(async () => {
    onChangePDFExportStatus('started')
    try {
      setLoading(true)

      // wait for initial render
      await new Promise((resolve) => {
        setTimeout(resolve, 1000)
      })

      const container = document.getElementById('printable_canvas')
      const fileName = `${property.name}_${layer}_map`

      const finalWidth = Math.max(width, 1400)
      const finalHeight = Math.max(height, 200)

      await html2pdf()
        .from(container)
        .set({
          filename: sanitizeFileName(fileName),
          html2canvas: { scale: 2 },
          jsPDF: {
            orientation: finalWidth > finalHeight ? 'landscape' : 'portrait',
            unit: 'px',
            hotfixes: ['px_scaling'],
            format: [finalWidth, finalHeight],
          },
        })
        .save()
    } finally {
      setLoading(false)
      onChangePDFExportStatus('finished')
    }
  }, [property, layer, width, height, onChangePDFExportStatus])

  return (
    <>
      <Menu
        id="map-export-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: -6 }}
        transformOrigin={{ vertical: -10, horizontal: 'center' }}
        slotProps={{
          paper: { sx: { width: 200 } },
          list: { sx: { py: 0 } },
        }}
      >
        <MenuItem
          dense
          divider
          onClick={() => {
            handleExportData()
            setAnchorEl(null)
          }}
          sx={{ py: 1.2 }}
        >
          <ListItemIcon>
            <ArticleOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Data</ListItemText>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            CSV
          </Typography>
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            handleExportMap()
            setAnchorEl(null)
          }}
          sx={{ py: 1.2 }}
        >
          <ListItemIcon>
            <ImageOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Map</ListItemText>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            PDF
          </Typography>
        </MenuItem>
      </Menu>

      <Button
        variant="outlined"
        size="small"
        loading={loading}
        disabled={disabled}
        startIcon={<FileDownload />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        {...rest}
      >
        Export
      </Button>
    </>
  )
}
